import React, { useEffect } from "react";
import { ArrowUpRight, Sparkles, MessageSquare, Globe2, ShieldCheck } from "lucide-react";

const FONT_LINK_ID = "landing-fonts";

const colors = {
  bg: "#eef2f6",
  ink: "#0f172a",
  muted: "#64748b",
  primary: "#0f2744",
  accent: "#0d9488",
  cardBg: "#f4f7fa",
  border: "#d0d9e3",
  white: "#ffffff",
};

function ChatbotPreviewCard() {
  return (
    <div
      className="relative w-full max-w-[340px] mx-auto flex flex-col overflow-hidden bg-white"
      style={{
        borderRadius: 18,
        border: `1px solid ${colors.border}`,
        boxShadow: "0 10px 28px rgba(15, 39, 68, 0.08)",
      }}
    >
      <div
        className="flex items-center justify-center px-5 py-3"
        style={{ backgroundColor: colors.primary }}
      >
        <span className="text-white font-semibold text-[15px] tracking-tight">
          revize.AI
        </span>
      </div>

      <div
        className="px-4 pt-4 pb-2 flex flex-col"
        style={{ backgroundColor: colors.cardBg }}
      >
        <p
          className="text-center text-[12px] leading-relaxed mb-3 px-1"
          style={{ color: colors.muted }}
        >
          Ask anything, and our AI chatbot will do its best to answer your
          question.
        </p>

        <div
          className="rounded-xl mb-3 p-3 flex gap-2 items-start text-[12px] leading-snug bg-white"
          style={{ border: `1px solid ${colors.border}`, color: colors.muted }}
        >
          <svg
            className="flex-none mt-0.5"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path d="M7.99994 12V7" stroke={colors.muted} strokeWidth="1.7" />
            <circle
              cx="1.1"
              cy="1.1"
              r="1.1"
              transform="matrix(1 0 0 -1 6.89993 6.2002)"
              fill={colors.muted}
            />
            <circle
              cx="8"
              cy="8"
              r="6.5"
              stroke={colors.muted}
              strokeWidth="1.75"
            />
          </svg>
          <span>
            This feature uses AI, and the information may not be accurate.
            Please verify it independently.
          </span>
        </div>

        <div className="flex items-end gap-2 mb-2">
          <Sparkles
            size={15}
            className="flex-none mb-2"
            style={{ color: colors.accent }}
            strokeWidth={1.75}
          />
          <div
            className="rounded-xl p-3 text-[12px] leading-relaxed bg-white"
            style={{
              color: colors.ink,
              borderBottomLeftRadius: 4,
              border: `1px solid ${colors.border}`,
            }}
          >
            Hello! I&apos;m an AI chatbot capable of addressing your inquiries.
            Feel free to ask any questions you may have!
            <div className="mt-1.5 text-[10px]" style={{ color: colors.muted }}>
              — AI chatbot
            </div>
          </div>
        </div>

        <div
          className="text-center text-[10px] py-2"
          style={{ color: "#94a3b8" }}
        >
          Powered by{" "}
          <span style={{ color: colors.primary }}>revize.AI</span>
          , the Website Experts
        </div>
      </div>

      <div className="px-4 pb-3.5" style={{ backgroundColor: colors.cardBg }}>
        <div
          className="flex items-center gap-2 w-full rounded-full px-3.5 py-2.5 text-[12px] bg-white"
          style={{ border: `1px solid ${colors.border}` }}
        >
          <Sparkles size={13} style={{ color: colors.accent }} strokeWidth={1.75} />
          <span className="flex-1" style={{ color: colors.muted }}>
            Ask a question...
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke={colors.primary}
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function LandingPage() {
  useEffect(() => {
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const features = [
    { icon: MessageSquare, label: "Instant answers" },
    { icon: Globe2, label: "Multilingual" },
    { icon: ShieldCheck, label: "Grounded in your site" },
  ];

  return (
    <div
      className="h-full w-full overflow-hidden flex flex-col"
      style={{
        fontFamily: "'Instrument Sans', system-ui, sans-serif",
        background: colors.bg,
        color: colors.ink,
      }}
    >
      <header className="flex-none flex items-center justify-between px-6 sm:px-10 lg:px-14 py-4 max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full"
            style={{ backgroundColor: colors.primary }}
          >
            <Sparkles size={14} className="text-white" strokeWidth={2} />
          </div>
          <span className="text-[15px] font-semibold tracking-tight">
            revize.AI
          </span>
        </div>
        <a
          href="https://www.revize.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] inline-flex items-center gap-1 hover:opacity-70"
          style={{ color: colors.muted }}
        >
          revize.com
          <ArrowUpRight size={13} strokeWidth={1.75} />
        </a>
      </header>

      <section className="flex-1 px-6 sm:px-10 lg:px-14 pb-4 max-w-7xl mx-auto w-full flex flex-col justify-center min-h-0">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="text-center lg:text-left order-2 lg:order-1">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}
            >
              revize.AI
            </h1>
            <p
              className="mt-2 text-lg sm:text-xl font-medium"
              style={{ color: colors.primary }}
            >
              AI chatbots for websites
            </p>
            <p
              className="mt-3 max-w-md mx-auto lg:mx-0 text-[15px] leading-relaxed"
              style={{ color: colors.muted }}
            >
              Embedded AI chat for your website—ready when visitors need
              answers. Open it from your site, or visit Revize to learn more.
            </p>
            <a
              href="https://www.revize.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: colors.accent }}
            >
              Visit revize.com
              <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <ChatbotPreviewCard />
          </div>
        </div>

        <div
          className="mt-8 pt-5 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-4 sm:gap-8 max-w-3xl mx-auto w-full"
          style={{ borderTop: `1px solid ${colors.border}` }}
        >
          {features.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2.5 text-sm font-medium"
              style={{ color: colors.ink }}
            >
              <Icon size={17} style={{ color: colors.accent }} strokeWidth={1.75} />
              {label}
            </div>
          ))}
        </div>
      </section>

      <footer
        className="flex-none px-6 sm:px-10 lg:px-14 py-4 max-w-7xl mx-auto w-full"
        style={{ borderTop: `1px solid ${colors.border}` }}
      >
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p style={{ color: colors.muted }} className="text-xs sm:text-sm">
            Powered by{" "}
            <a
              href="https://www.revize.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: colors.primary }}
            >
              revize.AI
            </a>
            <span className="hidden sm:inline"> — the Website Experts</span>
          </p>
          <div className="flex items-center gap-5 text-xs" style={{ color: "#94a3b8" }}>
            <a
              href="https://www.revize.com/ai_terms/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Terms of use
            </a>
            <a
              href="https://www.revize.com/ai_privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
